type Listener = () => void;

export type SelectionStore = {
  subscribeId: (id: string, listener: Listener) => () => void;
  getIsSelected: (id: string) => boolean;
  subscribeSummary: (listener: Listener) => () => void;
  getCount: () => number;
  getSelectedIds: () => string[];
  setOrderedIds: (ids: string[]) => void;
  select: (id: string, shiftKey: boolean) => void;
  selectMany: (ids: string[]) => void;
  deselectMany: (ids: string[]) => void;
  clear: () => void;
  reset: () => void;
};

export const createSelectionStore = (): SelectionStore => {
  const selected = new Set<string>();
  let orderedIds: string[] = [];
  let anchorId: string | null = null;

  const idListeners = new Map<string, Set<Listener>>();
  const summaryListeners = new Set<Listener>();

  const notifyId = (id: string) => {
    idListeners.get(id)?.forEach(listener => listener());
  };

  const notifySummary = () => {
    summaryListeners.forEach(listener => listener());
  };

  const toggleOne = (id: string) => {
    if (selected.has(id)) selected.delete(id);
    else selected.add(id);
    anchorId = id;
    notifyId(id);
    notifySummary();
  };

  const selectRange = (fromId: string, toId: string) => {
    const from = orderedIds.indexOf(fromId);
    const to = orderedIds.indexOf(toId);
    if (from === -1 || to === -1) {
      toggleOne(toId);
      return;
    }
    const [low, high] = from < to ? [from, to] : [to, from];
    const changed: string[] = [];
    for (let i = low; i <= high; i += 1) {
      const id = orderedIds[i];
      if (!selected.has(id)) {
        selected.add(id);
        changed.push(id);
      }
    }
    if (changed.length === 0) return;
    changed.forEach(notifyId);
    notifySummary();
  };

  return {
    subscribeId: (id: string, listener: Listener): (() => void) => {
      let listeners = idListeners.get(id);
      if (!listeners) {
        listeners = new Set();
        idListeners.set(id, listeners);
      }
      listeners.add(listener);
      return () => {
        listeners?.delete(listener);
        if (listeners && listeners.size === 0) idListeners.delete(id);
      };
    },
    getIsSelected: (id: string) => selected.has(id),

    subscribeSummary: (listener: Listener): (() => void) => {
      summaryListeners.add(listener);
      return () => summaryListeners.delete(listener);
    },
    getCount: () => selected.size,

    getSelectedIds: () => Array.from(selected),

    setOrderedIds: (ids: string[]) => {
      orderedIds = ids;
    },

    select: (id: string, shiftKey: boolean) => {
      if (shiftKey && anchorId && orderedIds.length > 0) {
        selectRange(anchorId, id);
        return;
      }
      toggleOne(id);
    },

    selectMany: (ids: string[]) => {
      const added: string[] = [];
      ids.forEach(id => {
        if (!selected.has(id)) {
          selected.add(id);
          added.push(id);
        }
      });
      if (added.length === 0) return;
      added.forEach(notifyId);
      notifySummary();
    },

    deselectMany: (ids: string[]) => {
      const removed: string[] = [];
      ids.forEach(id => {
        if (selected.has(id)) {
          selected.delete(id);
          removed.push(id);
        }
      });
      if (removed.length === 0) return;
      removed.forEach(notifyId);
      notifySummary();
    },

    clear: () => {
      if (selected.size === 0) return;
      const cleared = Array.from(selected);
      selected.clear();
      anchorId = null;
      cleared.forEach(notifyId);
      notifySummary();
    },

    reset: () => {
      selected.clear();
      orderedIds = [];
      anchorId = null;
      idListeners.forEach(listeners =>
        listeners.forEach(listener => listener()),
      );
      summaryListeners.forEach(listener => listener());
    },
  };
};
